import { ApiClient } from '@infrastructure/ApiClient';
import {
  ShipmentDTO,
  CreateShipmentRequestDTO,
  UpdateShipmentRequestDTO,
  ShipmentMethodDTO,
  CreateShipmentMethodRequestDTO,
  UpdateShipmentMethodRequestDTO
} from '../dto/ShipmentDTO';
import { TransportMethodDTO } from '@infrastructure/product/dto/TransportMethodDTO';

export class ShipmentDataSource {
  constructor(private api: ApiClient) { }

  async getShipments(filters?: {
    transportMethod?: TransportMethodDTO;
    departureDateFrom?: string;
    departureDateTo?: string;
    arrivalDateFrom?: string;
    arrivalDateTo?: string;
  }): Promise<ShipmentDTO[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.transportMethod) {
        params.append('transport_method', filters.transportMethod);
      }
      if (filters?.departureDateFrom) {
        params.append('departure_date_from', filters.departureDateFrom);
      }
      if (filters?.departureDateTo) {
        params.append('departure_date_to', filters.departureDateTo);
      }
      if (filters?.arrivalDateFrom) {
        params.append('arrival_date_from', filters.arrivalDateFrom);
      }
      if (filters?.arrivalDateTo) {
        params.append('arrival_date_to', filters.arrivalDateTo);
      }

      const queryString = params.toString();
      const url = queryString ? `/shipments?${queryString}` : '/shipments';

      const response = await this.api.get<ShipmentDTO[]>(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch shipments:', error);
      throw new Error('Failed to fetch shipments');
    }
  }

  async getShipment(id: string): Promise<ShipmentDTO> {
    try {
      const response = await this.api.get<ShipmentDTO>(`/shipments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch shipment:', error);
      throw new Error('Failed to fetch shipment');
    }
  }

  async createShipment(request: CreateShipmentRequestDTO): Promise<ShipmentDTO> {
    try {
      const response = await this.api.post<ShipmentDTO>('/shipments', request);
      return response.data;
    } catch (error) {
      console.error('Failed to create shipment:', error);
      throw new Error('Failed to create shipment');
    }
  }

  async updateShipment(id: string, request: Partial<UpdateShipmentRequestDTO>): Promise<ShipmentDTO> {
    try {
      const response = await this.api.put<ShipmentDTO>(`/shipments/${id}`, request);
      return response.data;
    } catch (error) {
      console.error('Failed to update shipment:', error);
      throw new Error('Failed to update shipment');
    }
  }

  async deleteShipment(id: string): Promise<void> {
    try {
      await this.api.delete(`/shipments/${id}`);
    } catch (error) {
      console.error('Failed to delete shipment:', error);
      throw new Error('Failed to delete shipment');
    }
  }

  async addPurchaseOrderItems(shipmentId: string, purchaseOrderItemIds: string[]): Promise<void> {
    try {
      const response = await this.api.post<void>(`/shipments/${shipmentId}/purchase-order-items`, {
        purchase_order_item_ids: purchaseOrderItemIds
      });
      return response.data;
    } catch (error) {
      console.error('Failed to add purchase order items to shipment:', error);
      throw new Error('Failed to add purchase order items to shipment');
    }
  }

  async removePurchaseOrderItems(shipmentId: string, purchaseOrderItemIds: string[]): Promise<void> {
    try {
      await this.api.delete(`/shipments/${shipmentId}/purchase-order-items`, {
        data: {
          purchase_order_item_ids: purchaseOrderItemIds
        }
      });
    } catch (error) {
      console.error('Failed to remove purchase order items from shipment:', error);
      throw new Error('Failed to remove purchase order items from shipment');
    }
  }

  // Shipment Methods API calls
  async getShipmentMethods(): Promise<ShipmentMethodDTO[]> {
    try {
      const response = await this.api.get<ShipmentMethodDTO[]>('/shipments/methods');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch shipment methods:', error);
      throw new Error('Failed to fetch shipment methods');
    }
  }

  async getShipmentMethod(id: string): Promise<ShipmentMethodDTO> {
    try {
      const response = await this.api.get<ShipmentMethodDTO>(`/shipments/methods/${id}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch shipment method:', error);
      throw new Error('Failed to fetch shipment method');
    }
  }

  async createShipmentMethod(request: CreateShipmentMethodRequestDTO): Promise<ShipmentMethodDTO> {
    try {
      const response = await this.api.post<ShipmentMethodDTO>('/shipments/methods', request);
      return response.data;
    } catch (error) {
      console.error('Failed to create shipment method:', error);
      throw new Error('Failed to create shipment method');
    }
  }

  async updateShipmentMethod(id: string, request: UpdateShipmentMethodRequestDTO): Promise<ShipmentMethodDTO> {
    try {
      const response = await this.api.put<ShipmentMethodDTO>(`/shipments/methods/${id}`, request);
      return response.data;
    } catch (error) {
      console.error('Failed to update shipment method:', error);
      throw new Error('Failed to update shipment method');
    }
  }

  async deleteShipmentMethod(id: string): Promise<void> {
    try {
      await this.api.delete(`/shipments/methods/${id}`);
    } catch (error) {
      console.error('Failed to delete shipment method:', error);
      throw new Error('Failed to delete shipment method');
    }
  }
} 